import { useEffect, useState } from "react";
import { Loader2, Search, User } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { StatusBadge } from "./ResourceBadges";
import { labelOf } from "./resourceApi";

function parseClientHit(hit) {
  const u = new URL(hit.href, window.location.origin);
  const email = decodeURIComponent(u.pathname.split("/clients/")[1] || "");
  return { email, canonicalId: u.searchParams.get("as") || null, name: hit.label };
}

function useDebounced(value, ms = 250) {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

export function ClientPicker({ onPick }) {
  const [q, setQ] = useState("");
  const [hits, setHits] = useState([]);
  const [loading, setLoading] = useState(false);
  const dq = useDebounced(q);
  useEffect(() => {
    if (dq.trim().length < 2) { setHits([]); return; }
    setLoading(true);
    api.get("/global-search", { params: { q: dq.trim(), kinds: "client" } })
      .then((r) => setHits((r.data.results || []).filter((h) => h.kind === "client")))
      .catch(() => setHits([]))
      .finally(() => setLoading(false));
  }, [dq]);
  return (
    <div className="space-y-2" data-testid="send-client-picker">
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-zinc-400" />
        <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search clients by name, email or phone"
               className="pl-8" data-testid="send-client-search" />
      </div>
      {loading && <div className="text-xs text-zinc-500"><Loader2 className="inline h-3 w-3 animate-spin" /> Searching…</div>}
      <ul className="max-h-64 divide-y divide-zinc-100 overflow-y-auto rounded-md border border-zinc-200">
        {hits.map((h, i) => (
          <li key={h.href}>
            <button type="button" onClick={() => onPick(parseClientHit(h))} data-testid={`send-client-option-${i}`}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-zinc-50">
              <User className="h-3.5 w-3.5 text-zinc-400" /><span className="font-medium">{h.label}</span>
              <span className="truncate text-xs text-zinc-500">{h.sublabel}</span>
            </button>
          </li>
        ))}
        {!loading && dq.trim().length >= 2 && hits.length === 0 && <li className="px-3 py-2 text-xs text-zinc-500">No clients found.</li>}
      </ul>
    </div>
  );
}

export function ResourcePicker({ meta, onPick }) {
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [rows, setRows] = useState(null);
  const dq = useDebounced(q);
  useEffect(() => {
    api.get("/clinical-resources", { params: { q: dq, category, audience: "client", status: "approved" } })
      .then((r) => setRows(r.data.items.filter((x) => x.can_send)))
      .catch(() => setRows([]));
  }, [dq, category]);
  return (
    <div className="space-y-2" data-testid="send-resource-picker">
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-zinc-400" />
        <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search approved client resources"
               className="pl-8" data-testid="send-resource-search" />
      </div>
      <div className="flex flex-wrap gap-1">
        {[{ key: "", label: "All" }, ...meta.categories].map((c) => (
          <button key={c.key || "all"} type="button" onClick={() => setCategory(c.key)} data-testid={`send-resource-cat-${c.key || "all"}`}
                  className={`rounded-full border px-2 py-0.5 text-2xs ${category === c.key ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-300 text-zinc-600"}`}>
            {c.label}
          </button>
        ))}
      </div>
      <ul className="max-h-64 divide-y divide-zinc-100 overflow-y-auto rounded-md border border-zinc-200">
        {rows === null && <li className="px-3 py-2 text-xs text-zinc-500">Loading…</li>}
        {rows?.map((r) => (
          <li key={r.id}>
            <button type="button" onClick={() => onPick(r)} data-testid={`send-resource-option-${r.id}`}
                    className="w-full px-3 py-2 text-left hover:bg-zinc-50">
              <div className="flex items-center gap-2 text-sm font-medium">{r.title} <StatusBadge status={r.status} />
                <span className="font-mono text-2xs text-zinc-500">v{r.current_version_number}</span></div>
              <div className="text-xs text-zinc-500">{labelOf(meta.types, r.type)} · {r.categories.map((c) => labelOf(meta.categories, c)).join(", ")}</div>
            </button>
          </li>
        ))}
        {rows?.length === 0 && <li className="px-3 py-2 text-xs text-zinc-500" data-testid="send-resource-empty">No approved client resources match.</li>}
      </ul>
    </div>
  );
}
